import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@/lib/api';
import { cn, Button } from './index';

/**
 * SHL-9. Four things the legacy toasts got wrong, fixed here:
 *  - identical consecutive errors produced exactly one toast, because the state
 *    variable never changed; here duplicates are COUNTED, not swallowed
 *  - `errorMessage` was never cleared, so the same error re-fired on unrelated
 *    state changes; here a toast owns its own lifetime
 *  - auth, network, validation and server failures all looked the same
 *  - nothing was ever actionable
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Repeat count for an identical toast, shown as a badge. */
  count: number;
  action?: { label: string; onClick: () => void };
  requestId?: string;
  durationMs: number;
}

/** What a caller supplies; id, count and duration are filled in for them. */
export type ToastInput = Omit<Toast, 'id' | 'count' | 'durationMs'> & { durationMs?: number };

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: ToastInput) => void;
  dismiss: (id: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  /** Turns any thrown error into the right toast, with the right wording. */
  fromError: (err: unknown, fallbackTitle?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const schedule = useCallback(
    (id: string, durationMs: number) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      if (durationMs === Infinity) return;
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), durationMs),
      );
    },
    [dismiss],
  );

  const push = useCallback<ToastContextValue['push']>(
    (input) => {
      const duration = input.durationMs ?? (input.tone === 'error' ? 8000 : DEFAULT_DURATION);
      // Deduplicate on content, not identity, so a repeated failure increments
      // a counter instead of stacking or -- worse -- being silently dropped.
      const key = `${input.tone}:${input.title}:${input.description ?? ''}`;

      setToasts((current) => {
        const existing = current.find((t) => t.id === key);
        if (existing) {
          schedule(key, duration);
          return current.map((t) => (t.id === key ? { ...t, count: t.count + 1 } : t));
        }
        schedule(key, duration);
        return [...current, { ...input, id: key, count: 1, durationMs: duration }];
      });
    },
    [schedule],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (title, description) =>
        push({ tone: 'success', title, ...(description ? { description } : {}) }),
      error: (title, description) =>
        push({ tone: 'error', title, ...(description ? { description } : {}) }),

      fromError: (err, fallbackTitle = 'Something went wrong') => {
        if (err instanceof ApiError) {
          // A dead session is handled by a redirect, not a toast.
          if (err.isAuthError) return;

          if (err.isNetworkError) {
            push({
              tone: 'error',
              title: 'Cannot reach the server',
              description: 'Check your connection and try again.',
            });
            return;
          }
          if (err.isForbidden) {
            push({
              tone: 'warning',
              title: 'Not allowed',
              description: err.message,
            });
            return;
          }
          if (err.status === 400 && err.fields) {
            // Field errors belong on the inputs; the toast just points there.
            push({
              tone: 'warning',
              title: 'Some fields need attention',
              description: Object.values(err.fields)[0],
            });
            return;
          }
          push({
            tone: 'error',
            title: err.status >= 500 ? 'Server error' : fallbackTitle,
            description: err.message,
            ...(err.requestId ? { requestId: err.requestId } : {}),
          });
          return;
        }

        push({
          tone: 'error',
          title: fallbackTitle,
          description: err instanceof Error ? err.message : undefined,
        });
      },
    }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-ok/40 bg-ok/10 text-ok',
  error: 'border-danger/40 bg-danger/10 text-danger',
  warning: 'border-warn/40 bg-warn/10 text-warn',
  info: 'border-info/40 bg-info/10 text-info',
};

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div
      // NFR-4: async results are announced to assistive tech.
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 end-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto rounded-lg border bg-surface p-3 shadow-lg',
            TONE_STYLES[toast.tone],
          )}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-medium">
                <span className="truncate">{toast.title}</span>
                {toast.count > 1 && (
                  <span className="shrink-0 rounded-full bg-current/20 px-1.5 text-[10px]">
                    ×{toast.count}
                  </span>
                )}
              </p>
              {toast.description && (
                <p className="mt-0.5 break-words text-xs text-fg/80">{toast.description}</p>
              )}
              {toast.requestId && (
                <p className="mt-1 font-mono text-[10px] text-muted">Ref: {toast.requestId}</p>
              )}
              {toast.action && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-1.5 -ms-2"
                  onClick={() => {
                    toast.action?.onClick();
                    onDismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </Button>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-1 text-muted hover:bg-raised hover:text-fg"
            >
              <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
